import React, { useState } from 'react';
import InputField from '../../components/inputs/Input';
import Button from '../../components/buttons/SimpleButton/Button';
import { Address, AddressFormProps, UpdateAddressResponse } from '../types/Address';
import { saveAddress } from '../services/ActivationService';
import { useTranslations } from '../../localization/useTranslations';
import { getDeliveryAddressType } from '../../utils/getDeliveryAddressType';

// Default dummy address to initialize the form
const dummyAddress: Address = {
  type: getDeliveryAddressType(window.country || 'cz'),
  email: window?.crm_user?.user?.email || '',
  company_name: '',
  phone_number: '',
  company_id: '',
  tax_id: '',
  vat_id: '',
  first_name: '',
  last_name: '',
  address: '',
  number: '',
  zip: '',
  city: '',
  country_iso: '',
};

const AddressForm: React.FC<AddressFormProps & { updateSnackbarMessage: (message: string) => void }> = ({
  onButtonClick,
  addressData,
  updateSnackbarMessage, // Receive the function as a prop
}) => {
  const translations = useTranslations();
  const [formValues, setFormValues] = useState<Address>({
    ...dummyAddress,
    ...addressData,
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const handleInputChange = (name: string, value: string) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, boolean> = {};
    const requiredFields = ['first_name', 'last_name', 'phone_number', 'address', 'number', 'city', 'zip'];

    requiredFields.forEach((field) => {
      if (!formValues[field as keyof Address]) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleButtonClick = async () => {
    if (!validateForm()) {
      updateSnackbarMessage('Please fill all required fields');
      return;
    }

    try {
      const updatedAddressData: Address = {
        ...addressData,
        ...formValues,
      };

      const updatedAddressForSave: UpdateAddressResponse = {
        ...updatedAddressData,
      };

      saveAddress(updatedAddressForSave).then((savedAddressResult) => {
        setFormValues({
          ...formValues,
          ...savedAddressResult,
        });

        onButtonClick(updatedAddressData);

        updateSnackbarMessage(
          translations?.snackBarMessages?.deliveryAddressSuccessfullyUpdated || 'Delivery address successfully updated'
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } catch (error) {
      updateSnackbarMessage(translations?.snackBarMessages?.somethingWentWrong || 'Error during button click');
    }
  };

  return (
    <div className="step-address__form">
      <InputField
        label={translations?.subscriptionActivation?.firstNameLabel || 'First name'}
        placeholder={translations?.general?.firstNamePlaceholder || ''}
        value={formValues.first_name}
        onChange={(e) => handleInputChange('first_name', e.target.value)}
        hasError={errors.first_name}
        autoFocus
      />
      <InputField
        label={translations?.subscriptionActivation?.lastNameLabel || 'Last name'}
        placeholder={translations?.general?.lastNamePlaceholder}
        value={formValues.last_name}
        onChange={(e) => handleInputChange('last_name', e.target.value)}
        hasError={errors.last_name}
      />
      <InputField
        label={translations?.subscriptionActivation?.phoneNumberLabel || 'Phone number'}
        placeholder={translations?.general?.phoneNumberPlaceholder || ''}
        value={formValues.phone_number}
        onChange={(e) => handleInputChange('phone_number', e.target.value)}
        hasError={errors.phone_number}
      />
      <InputField
        label={translations?.myAccountSettings?.streetName || 'Address'}
        placeholder={translations?.general?.addressPlaceholder || ''}
        value={formValues.address}
        onChange={(e) => handleInputChange('address', e.target.value)}
        hasError={errors.address}
      />
      <InputField
        label={translations?.myAccountSettings?.houseNumber || 'House number'}
        placeholder={translations?.myAccountSettings?.houseNumber || ''}
        value={formValues.number}
        onChange={(e) => handleInputChange('number', e.target.value)}
        hasError={errors.number}
      />
      <InputField
        label={translations?.subscriptionActivation?.cityLabel || 'City'}
        placeholder={translations?.general?.cityPlaceholder || ''}
        value={formValues.city}
        onChange={(e) => handleInputChange('city', e.target.value)}
        hasError={errors.city}
      />
      <InputField
        label={translations?.subscriptionActivation?.postalCodeLabel || 'ZIP code'}
        placeholder={translations?.general?.zipCodePlaceholder || ''}
        value={formValues.zip}
        onChange={(e) => handleInputChange('zip', e.target.value)}
        hasError={errors.zip}
      />
      <InputField
        label={translations?.subscriptionActivation?.companyNameLabel || 'Company name'}
        placeholder={translations?.general?.companyNamePlaceholder || ''}
        value={formValues.company_name}
        onChange={(e) => handleInputChange('company_name', e.target.value)}
        hasError={errors.company_name}
      />

      <div className="input-group button-group">
        <Button
          className="step-address__button"
          type="primary"
          size="large"
          fullWidth={true}
          label={translations?.subscriptionActivation?.activationButton || 'Activate Subscription'}
          onClick={handleButtonClick}
        />
      </div>
    </div>
  );
};

export default AddressForm;
